import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { useIntl } from 'react-intl';
import { useAtomValue, useSetAtom } from 'jotai';
import { openSnackbarAdviseAtom, userBranchesAtom } from '../../../Atoms';
import useAPI from '../../../api';
import FiltersNavbar from '../../Dashboard/FiltersNavbar';
import PivotWrapper from '../PivotWrapper';
import {
  DEFAULT_SLICE_CONFIGURATION_INBOUND,
  getMetadataInbound,
} from '../utils/constant';
import {
  formatPayloadDate,
  getThisMonthRange,
  getTodayRange,
  getYesterdayRange,
  getThisWeekRange,
  getLastWeekRange,
} from '../../../Helpers/dates';
import { ColumnFlexBox, FullSizeColumn } from '../../../Components/FlexBox';
import BranchCountersWidget from './Components/BranchCountersWidget';

const payloadPeriod = function payloadPeriod(per, start, end) {
  let returnRange = [start, end];
  if (per === 'range') return returnRange;
  else if (per === 'month') returnRange = getThisMonthRange();
  else if (per === 'today') returnRange = getTodayRange();
  else if (per === 'yesterday') returnRange = getYesterdayRange();
  else if (per === 'week') returnRange = getThisWeekRange();
  else if (per === 'lastWeek') returnRange = getLastWeekRange();
  return returnRange;
};

const PivotInboundReportPage = function PivotInboundReportPage({ userRoles }) {
  const API = useAPI();
  const intl = useIntl();
  const setOpenSnackbarAdvise = useSetAtom(openSnackbarAdviseAtom);
  const userBranches = useAtomValue(userBranchesAtom);

  const isOperator = Boolean(
    userRoles?.find(
      ({ roleId }) => roleId === 'OPERATOR' || roleId === 'LIMITEDOPERATOR'
    )
  );
  const [chartView, setChartView] = useState(
    isOperator ? true : localStorage.getItem('inboundPivotChartView') === 'true'
  );
  const [navbarOpen, setNavbarOpen] = useState(true);
  const [currentFilters, setCurrentFilters] = useState(null);
  const [pivotData, setPivotData] = useState(null);
  const [branchCounters, setBranchCounters] = useState(null);

  const handleToggleNavbar = () => {
    setNavbarOpen(!navbarOpen);
  };

  useEffect(() => {
    const savedFilters = localStorage.getItem('inboundPivotFilters');
    if (savedFilters) {
      setCurrentFilters(JSON.parse(savedFilters));
    } else {
      setCurrentFilters({
        periodName: 'today',
        startDate: new Date().toISOString(),
        endDate: new Date().toISOString(),
        grouping: { id: 'D', label: intl.messages.DAILY },
        branches:
          userBranches.length > 0 ? userBranches.map(({ id }) => id) : [],
      });
    }
  }, []);

  useEffect(() => {
    if (!currentFilters) return;

    const [start, end] = payloadPeriod(
      currentFilters?.periodName,
      currentFilters?.startDate,
      currentFilters?.endDate
    );
    API.getPivotData(
      formatPayloadDate(start)?.toISOString(),
      formatPayloadDate(end)?.toISOString(),
      currentFilters?.grouping?.id,
      {
        direction: 1, // inbound
        branchIds: currentFilters?.branches,
      },
      { silent: currentFilters?.autoRefresh },
      ({ pivotData, branchCounters }) => {
        setPivotData(
          pivotData?.map((x) => ({
            ...x,
            callOutcome:
              intl.messages[x?.callOutcome?.toUpperCase()] || x?.callOutcome,
            callHour: !isNaN(x?.callHour * 60 * 60)
              ? x?.callHour * 60 * 60
              : x?.callHour,
          }))
        );
        setBranchCounters(branchCounters);
        localStorage.setItem(
          'inboundPivotFilters',
          JSON.stringify(currentFilters)
        );
      },
      (err) => {
        setOpenSnackbarAdvise({
          message: intl.messages.ERROR_REPORT_LOADING,
          severity: 'error',
        });
        console.error(err);
      }
    );
  }, [currentFilters]);

  const toggleChartView = () => {
    setChartView(!chartView);
    localStorage.setItem('inboundPivotChartView', JSON.stringify(!chartView));
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      <FiltersNavbar
        hideBreadcrumbs
        title="INBOUND_CALLS_REPORT"
        toggle={handleToggleNavbar}
        open={navbarOpen}
        currentFilters={currentFilters}
        setCurrentFilters={setCurrentFilters}
        switchValue={chartView}
        switchLabel={
          !chartView ? intl.messages.PIVOT_VIEW : intl.messages.CHART_VIEW
        }
        onSwitchClick={isOperator ? undefined : toggleChartView}
        isDataLimited
        hideOperatorFilter
        isOperator={isOperator}
      />
      <FullSizeColumn overflow="auto" padding="24px">
        <ColumnFlexBox
          display={!chartView ? 'none!important' : 'inherit!important'}
        >
          {branchCounters && (
            <BranchCountersWidget branchCounters={branchCounters} />
          )}
        </ColumnFlexBox>
        <FullSizeColumn
          display={!chartView ? 'flex!important' : 'none!important'}
        >
          <PivotWrapper
            pivotType={1}
            visible={!chartView}
            data={pivotData}
            slice={DEFAULT_SLICE_CONFIGURATION_INBOUND}
            getMetadata={getMetadataInbound}
            localStorageKey="pivotConfigurationInbound"
            userRoles={userRoles}
          />
        </FullSizeColumn>
      </FullSizeColumn>
    </Box>
  );
};

export default PivotInboundReportPage;
